/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page de contact du site UK.
 *
 * La saisie est validee cote client par un schema zod avant tout appel reseau :
 * le visiteur voit ses erreurs immediatement, et le backend revalide de son
 * cote — la validation du navigateur ne protege rien a elle seule.
 *
 * Le message est enregistre en base meme si le courriel ne part pas : l'API
 * renvoie alors `email_sent: false`, et rien n'est perdu.
 */
import React, { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Phone, MapPin, Globe, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  email: z.string().email(),
  company: z.string().optional(),
  phone: z.string().optional(),
  subject: z.string().min(2),
  message: z.string().min(10),
});

const Contact = () => {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  if (!t) return null;
  const meta = t.meta.pages.contact;

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const { data: result } = await axios.post(`${API}/contact`, { ...data, language: lang });
      // The API answers 200 as soon as the message is stored, even when the
      // notification email could not be sent - in which case nobody at Prenim
      // is alerted. Announcing "message sent" then would be false, and the
      // visitor would wait for an answer that never comes.
      if (result?.email_sent === false) {
        toast.warning(t.common.message_not_delivered, { duration: 10000 });
      } else {
        toast.success(t.common.message_sent);
      }
      reset();
    } catch (e) {
      toast.error(t.common.message_error);
    } finally {
      setSubmitting(false);
    }
  };

  // OSM bbox covering both Leeds (UK) and N'Djamena (Chad). Markers placed on each.
  const ukMapSrc =
    "https://www.openstreetmap.org/export/embed.html?bbox=-1.575,53.8,-1.49,53.84&layer=mapnik&marker=53.823,-1.5345";
  const tdMapSrc =
    "https://www.openstreetmap.org/export/embed.html?bbox=15.02,12.08,15.10,12.13&layer=mapnik&marker=12.1067,15.0444";

  return (
    <main data-testid="contact-page" className="pt-20">
      <SEO title={meta.title} description={meta.description} lang={lang} />

      <section className="bg-slate-950 text-white py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
            {t.contact.hero.eyebrow}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
            {t.contact.hero.title}
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl">{t.contact.hero.subtitle}</p>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit(onSubmit)}
              data-testid="contact-form"
              className="bg-white rounded-2xl border border-slate-200 p-8 space-y-5"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="firstName" className="mb-2 block">{t.contact.form.first_name} *</Label>
                  <Input id="firstName" data-testid="contact-input-first-name" {...register("firstName")} />
                  {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName" className="mb-2 block">{t.contact.form.last_name} *</Label>
                  <Input id="lastName" data-testid="contact-input-last-name" {...register("lastName")} />
                  {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>}
                </div>
                <div>
                  <Label htmlFor="email" className="mb-2 block">{t.contact.form.email} *</Label>
                  <Input id="email" type="email" data-testid="contact-input-email" {...register("email")} />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <Label htmlFor="company" className="mb-2 block">{t.contact.form.company}</Label>
                  <Input id="company" data-testid="contact-input-company" {...register("company")} />
                </div>
                <div>
                  <Label htmlFor="phone" className="mb-2 block">{t.contact.form.phone}</Label>
                  <Input id="phone" data-testid="contact-input-phone" {...register("phone")} />
                </div>
              </div>
              <div>
                <Label htmlFor="subject" className="mb-2 block">{t.contact.form.subject} *</Label>
                <Input id="subject" data-testid="contact-input-subject" {...register("subject")} />
                {errors.subject && <p className="text-xs text-red-600 mt-1">{errors.subject.message}</p>}
              </div>
              <div>
                <Label htmlFor="message" className="mb-2 block">{t.contact.form.message} *</Label>
                <Textarea id="message" rows={6} data-testid="contact-input-message" {...register("message")} />
                {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message.message}</p>}
              </div>

              <p className="text-xs text-slate-500">{t.contact.form.consent}</p>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-slate-950 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 transition-colors"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {submitting ? t.common.sending : t.contact.form.submit}
              </button>
            </form>
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <h2 className="text-2xl font-bold text-slate-950">{t.contact.offices_title}</h2>
            {[t.contact.uk, t.contact.td].map((office, i) => (
              <div
                key={i}
                data-testid={`contact-office-${i}`}
                className="rounded-2xl border border-slate-200 p-6 bg-white"
              >
                <h3 className="text-lg font-bold text-slate-950 mb-3">{office.title}</h3>
                <p className="flex items-start gap-2 text-sm text-slate-600 mb-2">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
                  {office.address}
                </p>
                <p className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <Phone className="h-4 w-4 shrink-0 text-blue-600" />
                  <a href={`tel:${office.phone.replace(/\s/g, "")}`} className="hover:text-blue-600">{office.phone}</a>
                </p>
                {office.phone2 && (
                  <p className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                    <Phone className="h-4 w-4 shrink-0 text-blue-600" />
                    <a href={`tel:${office.phone2.replace(/\s/g, "")}`} className="hover:text-blue-600">{office.phone2}</a>
                  </p>
                )}
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="h-4 w-4 shrink-0 text-blue-600" />
                  <a href={`mailto:${office.email}`} className="hover:text-blue-600">{office.email}</a>
                </p>
                {office.website && (
                  <p className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                    <Globe className="h-4 w-4 shrink-0 text-blue-600" />
                    {office.website}
                  </p>
                )}
              </div>
            ))}
          </aside>
        </div>
      </section>

      <section data-testid="contact-maps" className="pb-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-950">Leeds, UK</span>
              <a
                href="https://www.openstreetmap.org/?mlat=53.823&mlon=-1.5345#map=14/53.823/-1.5345"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline"
              >
                OpenStreetMap
              </a>
            </div>
            <iframe
              data-testid="map-uk"
              title="Leeds map"
              src={ukMapSrc}
              className="w-full h-72 border-0"
              loading="lazy"
            />
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-950">N'Djamena, Tchad</span>
              <a
                href="https://www.openstreetmap.org/?mlat=12.1067&mlon=15.0444#map=14/12.1067/15.0444"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline"
              >
                OpenStreetMap
              </a>
            </div>
            <iframe
              data-testid="map-td"
              title="N'Djamena map"
              src={tdMapSrc}
              className="w-full h-72 border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
